import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import clsx from 'clsx';
import dayjs from 'dayjs';
import advanced from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import { useSession } from 'next-auth/react';
import party from 'party-js';
import React, { useRef } from 'react';
import config from '../config';
import { useAlgo } from '../hooks/useAlgo';
import { useKittiesStore } from '../hooks/useKittiesStore';
import useNfd from '../hooks/useNfd';
import { useVendingMachine } from '../hooks/useVendingMachine';
import formatNumber from '../utils/formatNumber';
import { VM_TOKEN_AMOUNT_NEEDED } from '../utils/vending-machine';
import Alert from './Alert';
import Button from './Button';
import ConnectDiscordMessage from './ConnectDiscordMessage';
import ConnectWalletMessage from './ConnectWalletMessage';
import Countdown from './Countdown';
import FullScreenLoader from './FullScreenLoader';
import NftImage from './NftImage';
import Spinner from './Spinner';

dayjs.extend(timezone);
dayjs.extend(advanced);
dayjs.tz.setDefault('America/New_York');

function TheVendingMachinePage() {
  const confettiRef = useRef();
  const { address } = useAlgo();
  const { nfd } = useNfd({ address });
  const { myKitties, myLegendaryKitties } = useKittiesStore();
  const { data: session } = useSession();
  const {
    vmAssetsQuery,
    vmAssetPreviews,
    vmAssetsCount,
    vmPurchaseCount,
    vmLastPurchase,
    memeCoinValue,
    canPurchase,
    discountedCost,
    correctDiscordAccount,
    enabledOnDate,
    formattedEnabledOnDate,
    isEnabledBasedOnDate,
    winnings,
    purchaseMutate,
    oddsOfWinning,
    vmTokenBalance,
  } = useVendingMachine();

  const shouldSpendVmTokens = vmTokenBalance >= VM_TOKEN_AMOUNT_NEEDED;
  const costText = shouldSpendVmTokens ? `Spend ${VM_TOKEN_AMOUNT_NEEDED} VM Tokens` : `Spend ${formatNumber(discountedCost)} Treats`;

  return (
    <div>
      <FullScreenLoader loading={purchaseMutate.isLoading} label='Processing your purchase...' />

      <div className='grid md:grid-cols-2 gap-10 mt-12 md:mt-20'>
        <div className='order-2 md:order-1'>
          <div className='grid grid-cols-2 sm:grid-cols-4 gap-4'>
            <NftPreview assetId={parseInt(process.env.NEXT_PUBLIC_SVMT_ID)} />
            <NftPreview assetId={2217748679} />
            {config.vmMemeCoins.map((coin) => (
              <div className='aspect-square relative bg-gray-900 rounded-lg' key={coin.assetId}>
                <img src={coin.image} alt={coin.name} className='object-cover w-full h-full rounded-lg' />
                <div className='absolute bottom-1 left-0 right-0 flex justify-center items-center'>
                  <p className='text-center text-sm font-medium text-gray-200  bg-gray-900 rounded-lg px-2 py-0.5'>{coin.name}</p>
                </div>
              </div>
            ))}
            {vmAssetPreviews.length > 0
              ? vmAssetPreviews.map((preview) => <NftPreview key={preview['asset-id']} assetId={preview['asset-id']} />)
              : [1, 2, 3, 4, 5, 6].map((i) => <div key={i} className='aspect-square relative bg-gray-900 rounded-lg' />)}
          </div>
          <div className='flex flex-col gap-5 mt-6 items-center'>
            <div className='flex gap-4'>
              <Button
                theme='none'
                href={`https://explorer.perawallet.app/address/${process.env.NEXT_PUBLIC_VENDING_MACHINE_WALLET}`}
                target={'_blank'}
                className='font-medium underline opacity-60'
              >
                View all NFTs
              </Button>

              <Button theme='none' href={`/donations`} className='font-medium underline opacity-60'>
                Donate to the Shitty Vault
              </Button>
            </div>
          </div>
        </div>
        <div className='flex flex-col gap-5 w-full bg-gray-900 rounded-lg px-6 pb-6 pt-16 relative overflow-hidden order-1 md:order-2'>
          <div className='absolute top-0 left-0 right-0 flex justify-center'>
            <div className={clsx('sm:rounded-b-lg px-2 py-1 w-full sm:w-auto sm:min-w-[325px] text-center bg-gray-700')}>
              {isEnabledBasedOnDate ? (
                <span>Vending Machine is open!</span>
              ) : (
                <>
                  <span className='opacity-60'>Opens in </span>{' '}
                  <strong>
                    <Countdown simple date={enabledOnDate} />
                  </strong>
                </>
              )}
            </div>
          </div>

          {!isEnabledBasedOnDate && (
            <div className=''>
              <StartsOn value={formattedEnabledOnDate} />
            </div>
          )}

          {!vmAssetsQuery.isLoading && (
            <div className='flex flex-col gap-6'>
              <div className='w-full flex gap-6 justify-center flex-wrap'>
                <Stat label='NFTs Left' value={vmAssetsCount} />
                <Stat label='Meme Coin Value' value={`$${memeCoinValue.toFixed(2)}`} />
              </div>
              <div className='w-full flex gap-6 justify-center flex-wrap'>
                {vmPurchaseCount < 2 && !shouldSpendVmTokens && <Stat label='Cost (Treats)' value={formatNumber(discountedCost)} />}
                {shouldSpendVmTokens && <Stat label='Cost (VM Tokens)' value={`${VM_TOKEN_AMOUNT_NEEDED}`} />}
                <Stat label='Odds of Winning' value={`${oddsOfWinning * 100}%`} />
                <Stat label='Purchases' value={`${vmPurchaseCount || 0}/2`} />
              </div>
            </div>
          )}

          <Requirements
            address={address}
            session={session}
            nfd={nfd}
            myKitties={myKitties}
            myLegendaryKitties={myLegendaryKitties}
            correctDiscordAccount={correctDiscordAccount}
            vmTokenBalance={vmTokenBalance}
          />

          <div ref={confettiRef}></div>
          {winnings && (
            <div className='flex flex-col items-center justify-center bg-gray-900 rounded-lg px-12 py-8'>
              <h2 className='mb-2 text-center font-bold text-2xl'>🎉 Thanks for your purchase! 🎉</h2>
              <div className='max-w-sm mx-auto mt-4'>
                {winnings.image ? (
                  <a href={`https://www.downbad.farm/asset/${winnings?.index}`} target='_blank'>
                    <img src={winnings.image} alt='prize' />
                  </a>
                ) : (
                  <Button theme='outline' href={`https://www.downbad.farm/asset/${winnings?.index}`} target='_blank'>
                    View NFT
                  </Button>
                )}
              </div>
            </div>
          )}
          {/* {myKitties.length === 0 && (
            <div className='mb-1'>
              <Alert type='danger'>🚨 You must own a Shitty Kitty to use the Shitty Vending Machine.</Alert>
            </div>
          )} */}
          {isEnabledBasedOnDate && (
            <>
              {vmAssetsQuery.isLoading ? (
                <div>
                  <Spinner dimensionClasses='w-6 h-6' />
                </div>
              ) : vmAssetsCount === 0 ? (
                <Alert type='danger' className='text-center'>
                  Sorry but all NFTs have been purchased. Check back at a later time.
                </Alert>
              ) : (
                <></>
              )}

              {!vmAssetsQuery.isLoading && (
                <div className='flex justify-center'>
                  {address && session?.user?.discord?.id ? (
                    <div>
                      <Button
                        loading={purchaseMutate.isLoading}
                        disabled={vmAssetsQuery.isLoading || !canPurchase || purchaseMutate.isLoading}
                        onClick={() =>
                          purchaseMutate.mutate(_, {
                            onSuccess: () => {
                              party.confetti(confettiRef.current, {
                                count: party.variation.range(20, 60),
                              });
                            },
                          })
                        }
                      >
                        {vmPurchaseCount < 2 ? costText : 'Max purchases'}
                      </Button>
                    </div>
                  ) : !address ? (
                    <div>
                      <ConnectWalletMessage />
                    </div>
                  ) : (
                    <div>
                      <ConnectDiscordMessage />
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {vmPurchaseCount === 2 && vmLastPurchase && (
            <p className='mt-0 text-gray-500 text-center'>Next purchase can be made {dayjs(vmLastPurchase).fromNow()}.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export function Requirements({ address, session, nfd, myKitties, myLegendaryKitties, correctDiscordAccount, vmTokenBalance }) {
  return (
    <div className='flex flex-col gap-3 justify-center'>
      <div className='flex flex-wrap gap-3 justify-center'>
        <Requirement
          label='Wallet'
          value={
            address ? (
              <span className='text-primary-500'>
                {address?.slice(0, 4)}...${address?.slice(-4)}
              </span>
            ) : (
              '❌'
            )
          }
        />
        <Requirement
          label='Discord'
          value={
            session?.user?.discord?.id ? (
              correctDiscordAccount ? (
                <span className='text-primary-500'>{session.user.discord.name}</span>
              ) : (
                <span className='text-red-500'>Registered to different user</span>
              )
            ) : (
              '❌'
            )
          }
        />
        <Requirement label='NFD' value={!!nfd ? <span className='text-primary-500'>{nfd?.name}</span> : '❌'} />
      </div>
      <div className='flex flex-wrap gap-3 justify-center'>
        <Requirement label='Total Kitties' value={myKitties.length > 0 ? <span className='text-primary-500'>{myKitties.length}</span> : '❌'} />
        <Requirement
          label='Legendary Kitties'
          value={myLegendaryKitties.length > 0 ? <span className='text-primary-500'>{myLegendaryKitties.length}</span> : '❌'}
        />
        <Requirement label='VM Tokens' value={vmTokenBalance ? <span className='text-primary-500'>{vmTokenBalance}</span> : '❌'} />
      </div>
    </div>
  );
}

export function Requirement({ label, value }) {
  return (
    <div className='flex items-center gap-2 border border-gray-600 rounded-md px-2 py-0.5 text-sm'>
      <span className='opacity-60'>{label}</span>
      <span className=''>{value}</span>
    </div>
  );
}

export function NftPreview({ assetId }) {
  const query = useQuery({
    queryKey: ['vm-preview', assetId],
    queryFn: async () => {
      const apiUrl = `https://mainnet-idx.algonode.cloud/v2/assets/${assetId}`;
      const { data } = await axios.get(apiUrl);
      return data;
    },
    enabled: !!assetId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
  });

  const data = query.data?.asset;

  return (
    <div className='aspect-square relative bg-gray-900 rounded-lg'>
      {!query.isLoading && data && <NftImage nft={data} className='object-cover w-full h-full rounded-lg' />}
    </div>
  );
}

export function Stat({ label, value }) {
  return (
    <div className='flex flex-col items-center'>
      <span className='font-medium text-text opacity-60'>{label}</span>
      <span className='text-xl font-bold text-primary-500'>{value}</span>
    </div>
  );
}

export function StartsOn({ label, value, children, success }) {
  return (
    <p className='flex flex-col items-center justify-center'>
      <span className='font-medium text-text opacity-60'>Starts on</span>{' '}
      <span className={clsx('text-lg font-bold', success ? 'text-green-500' : 'text-text')}>{children || value}</span>
    </p>
  );
}

export default TheVendingMachinePage;
